import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import type { ManyOfManyPollResponse } from '@tunasong/schemas'
import { isManyOfManyPoll } from '@tunasong/schemas'
import {  EntityLinkById, Typography, Checkbox, FormGroup, FormControl, FormControlLabel} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { getProgress } from '../progress-util.js'
import Progress from '../progress.js'
import { sortPollOptionsIndices } from '../sort.js'

export const ManyOfManyPollFinal: FC<PollProps> = props => {
  const { poll, onChange } = props

  invariant(isManyOfManyPoll(poll), `Poll must be a ManyOfManyPoll`)

  const { isCreatingEntity, hasCreatedEntity } = graphHooks.useEntityGraph()

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  invariant(isManyOfManyPoll(poll), 'Poll must be many of many')

  const handleChange = (idx: number) => (ev: React.ChangeEvent<HTMLInputElement>) => { const val = ev.target.checked;
    invariant(hasLoadedChildren, 'Children should be loaded')

    invariant(onChange)

    const { finalOption } = poll
    const updatedPoll = { ...poll, finalOption: { ...finalOption, [idx]: val } }
    onChange(updatedPoll)
  }

  const { name = 'Untitled', finalOption, closed } = poll

  const readOnly = Boolean(!Boolean(onChange) || closed || !hasLoadedChildren || isCreatingEntity || hasCreatedEntity)

  // Sort pollOptions by number  of votes in allResponses
  const pollOptionsIdx = sortPollOptionsIndices(poll, allResponses, 'desc')

  if (!hasLoadedChildren) {
    return <div>Loading...</div>
  }

  return (
    <div className={styles.container}>
      <Typography className={styles.title} variant="subtitle1">
        <PollIcon /> {name}
      </Typography>
      {closed && (
        <Typography variant="caption" color="secondary">
          The Poll is closed.
        </Typography>
      )}

      <FormControl className={styles.formControl}>
        <FormGroup>
          {hasLoadedChildren &&
            pollOptionsIdx.map(option =>
              poll.closed && !finalOption?.[option] ? null : (
                <FormControlLabel
                  className={styles.formLabel}
                  key={option}
                  value={option}
                  control={
                    closed ? (
                      <></>
                    ) : (
                      <Checkbox
                        disabled={readOnly || closed}
                        checked={Boolean(finalOption?.[option])}
                        onChange={handleChange(option)}
                      />
                    )
                  }
                  label={
                    <div className={cn(styles.optionBox)}>
                      {poll.pollOptions[option].entityRef ? (
                        <EntityLinkById entityId={poll.pollOptions[option].entityRef} />
                      ) : (
                        <Typography variant="subtitle1" color="primary">
                          {poll.pollOptions[option].name}
                        </Typography>
                      )}
                      <Progress value={getProgress(poll, allResponses, option)} />
                    </div>
                  }
                />
              )
            )}
        </FormGroup>
      </FormControl>
    </div>
  )
}
